import React, { useState } from 'react';
import CheckIcon from '../icons/CheckIcon';
import { Input } from '@mantine/core';

interface Option {
  label: string;
  value: string | boolean;
}

interface SelectableOptionsProps {
  options: (string | Option)[];
  icons?: React.ReactNode[];
  className?: string;
  value?: string | boolean;
  onChange?: (selected: string) => void;
  label?: string;
  WithAsterisk?: boolean;
  optionClassname?: string;
  error?: string;
  classNames?: any;
  disabledOptions?: string[];
}

const SelectableOptions: React.FC<SelectableOptionsProps> = ({
  options,
  icons = [],
  value,
  onChange,
  label = '',
  WithAsterisk = false,
  className,
  classNames,
  optionClassname,
  error,
  disabledOptions = [],
}) => {
  const [selected, setSelected] = useState<string | boolean | null>(
    value || null,
  );

  const handleSelect = (optionValue: string) => {
    setSelected(optionValue);
    onChange?.(optionValue);
  };

  const getOptionLabel = (option: string | Option) => {
    return typeof option === 'string' ? option : option.label;
  };

  const getOptionValue = (option: string | Option) => {
    return typeof option === 'string' ? option : option.value;
  };

  return (
    <Input.Wrapper
      label={label}
      withAsterisk={WithAsterisk}
      classNames={classNames}
      className={`flex flex-col items-start w-full text-start ${className || ''}`}>
      <div className="flex items-center w-full gap-[10px] text-[16px] flex-wrap">
        {options.map((option, index) => {
          const optionLabel = getOptionLabel(option);
          const optionValue = getOptionValue(option);
          const isDisabled = disabledOptions.includes(String(optionValue));
          return (
            <div
              key={optionValue}
              className={`flex relative flex-grow border rounded-[8px] border-[#A2AFBE] min-h-[44px] items-center justify-between  px-[15px] ${
                isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              } ${
                selected === optionValue ? 'bg-[#17385D] text-[white]' : ''
              } ${optionClassname}`}
              onClick={() => !isDisabled && handleSelect(optionValue)}>
              <div className="flex gap-[10px] items-center">
                <div
                  className={`h-[20px] w-[20px] flex-shrink-0 flex items-center justify-center rounded-full border border-[#A2AFBE] ${
                    selected === optionValue ? 'bg-[white]' : ''
                  }`}>
                  {selected === optionValue && <CheckIcon />}
                </div>
                <span>{optionLabel}</span>
              </div>
              {icons[index] && (
                <div className="mr-[10px]">
                  {React.cloneElement(icons[index] as React.ReactElement, {
                    fillColor: selected === optionValue ? 'white' : undefined,
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <div className="text-[#FA5252] text-[13.5px] mt-1">{error}</div>
      )}
    </Input.Wrapper>
  );
};

export default SelectableOptions;
